import APIV5 from '~/models/api';
import ModalUploadingController from '~/components/upload/modal/modal-uploading.controller';
import templateModalUploadClassic from '~/components/upload/modal/modal-uploading.html';
import FileUpload from '~/scripts/services/model/file-upload';

/* globals Knitiv */

export default class PredictDoctypeController {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').IIntervalService} $interval
	 * @param {import('angular').IQService} $q
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {any} Notification
	 */
	constructor(
		$injector,
		$interval,
		$q,
		$rootScope,
		$timeout,
		$translate,
		$uibModal,
		Notification,
	) {
		this.$injector = $injector;
		this.$interval = $interval;
		this.$q = $q;
		this.$rootScope = $rootScope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$uibModal = $uibModal;
		this.Notification = Notification;

		(async () => this.loadModels())();

		/** @type {boolean} */
		this.async = false;

		/** @type {import('angular').IPromise<any> | undefined} */
		this.check = undefined;

		/** @type {boolean} */
		this.deleteFile = false;

		/** @type {boolean} */
		this.loading = false;

		/** @type {any[]} */
		this.models = [];

		/** @type {boolean} */
		this.showDocTypes = false;
	}

	/**
	 * @param {any} model
	 * @param {File[]|FileList[]} files
	 */
	addFiles(model, files) {
		if (!model.files) {
			model.files = [];
		}
		for (const file of files) {
			if (file instanceof FileList) {
				Array.prototype.forEach.call(file, item => {
					model.files.push(new FileUpload(this.$injector, item));
				});
			}
			else {
				model.files.push(new FileUpload(this.$injector, file));
			}
		}
	}

	/**
	 * @param {number} jobId
	 * @param {any} model
	 */
	async checkJob(jobId, model) {
		/** @type {any} */
		const job = await APIV5.instance.kiaDocTypeJobInfo({
			job_id: jobId,
		}, {
			id: 'predict-doctype.check-job',
		});
		this.$timeout(() => {
			if (job.status === 'finished' && 'documents' in job.response) {
				if (this.check) {
					this.$interval.cancel(this.check);
				}
				this.setDocTypesFromResult(model, job.response.documents);

				if (job.response?.status) {
					model.status = job.response.status;
				}
				if (model.status === 'ready' && this.check) {
					this.$interval.cancel(this.check);
				}
			}
		});
	}

	async loadModels() {
		const result = await APIV5.instance.kiaDocTypeModelList({}, { id: 'admin-kia-doctype.load-databases' });
		this.$timeout(() => {
			// @ts-ignore
			this.models = result.models
				.filter((/** @type {any} */ model) => this.$rootScope.context?.dsnasso.kid === model.dsnasso_kid
					&& model.status === 'ready')
				.map((/** @type {any} */ model) => ({
					...model,
					files: [],
				}));
		});
	}

	/**
	 * @param {any} model
	 * @param {any[]} documents
	 */
	setDocTypesFromResult(model, documents) {
		model.files.map((/** @type {FileUpload} */ f) => {
			const predictResultDocument = documents.find(
				(/** @type {any} */ d) => d.path.split('/').at(-1) === f.id,
			);
			if (predictResultDocument) {
				f.isa = predictResultDocument['doctype-predicted'];
			}
			return f;
		});
	}

	/**
	 * @param {any} model
	 */
	async upload(model) {
		try {
			if (model.files.length > 0) {
				this.loading = true;
				const modalInstance = this.$uibModal.open({
					ariaLabelledBy:  'modal-uploading-title',
					ariaDescribedBy: 'modal-uploading-body',
					backdrop:        false,
					controller:      ModalUploadingController,
					controllerAs:    '$ctrl',
					template:        templateModalUploadClassic,
					size:            'sm',
					resolve:         {
						files: () => model.files,
					},
					windowClass:     'modal-upload',
				});
				const opened = await modalInstance.opened;

				if (opened) {
					// eslint-disable-next-line unicorn/no-array-reduce
					const result = await model.files.reduce(
						(/** @type {Promise<any>} */ p, /** @type {any} */ file) => p
							.then(() => file.upload()),
						this.$q.when(true),
					);

					if (result) {
						this.Notification.success({
							title:   this.$translate.instant('import.imported-files-title'),
							message: this.$translate.instant('import.imported-files-message'),
						});

						this.$timeout(() => {
							modalInstance.close();
						}, 1000);

						const body = {
							documents: model.files.map((/** @type {FileUpload} */ f) => f.id),
							model_id: model.id,
						};
						const options = {
							id: 'kia-doctype.submit',
						};

						const predictResult = this.async
							? await APIV5.instance.kiaDocTypeModelPredictAsynchrone(body, options)
							: await APIV5.instance.kiaDocTypeModelPredictSynchrone(body, options);

						if (predictResult) {
							if (this.async && 'job_id' in predictResult) {
								const jobId = predictResult.job_id;
								this.check = this.$interval(
									() => this.checkJob(jobId, model),
									2000,
								);
							}
							else if ('documents' in predictResult) {
								this.setDocTypesFromResult(model, predictResult.documents);
							}
						}
					}
				}
			}
		}
		catch (/** @type {any} */ error) {
			let message = this.$translate.instant('error.file-not-imported-error');

			if ('status' in error && error.status === -1) {
				message = this.$translate.instant('error.network-disconnected');
			}
			console.error(error);
			this.loading = false;

			this.Notification.warning({
				title:    this.$translate.instant('error.file-not-imported-title'),
				template: message,
			});
		}
	}
}
